import { StoredPacket, getDb } from '../storage/db';
import { MapManager } from './mapManager';

export interface FilterState {
  isolatedNodeId: number | null;
  minSnr: number;
  sessionId: string | null;
}

/**
 * Filter engine providing Node-Isolation (sender & 8-bit relay matching),
 * SNR thresholding, and session filtering over Leaflet map layers.
 */
export class FilterEngine {
  private currentFilter: FilterState = {
    isolatedNodeId: null,
    minSnr: -25,
    sessionId: null
  };

  constructor(private mapManager: MapManager) {}

  /**
   * Sets the target node ID for Node-Isolation mode.
   * Filters map to packets sent by this node OR relayed by this node via (nodeId & 0xFF).
   *
   * @param nodeId - Target 32-bit node ID or null to reset isolation
   */
  async setIsolatedNode(nodeId: number | null): Promise<void> {
    this.currentFilter.isolatedNodeId = nodeId;
    await this.applyFilters();
  }

  /**
   * Sets the minimum SNR threshold in dB.
   *
   * @param minSnr - Threshold value in dB
   */
  async setMinSnr(minSnr: number): Promise<void> {
    this.currentFilter.minSnr = minSnr;
    await this.applyFilters();
  }

  /**
   * Sets the active session filter.
   *
   * @param sessionId - Session identifier or null for all sessions
   */
  async setSession(sessionId: string | null): Promise<void> {
    this.currentFilter.sessionId = sessionId;
    await this.applyFilters();
  }

  /**
   * Tests whether an individual packet satisfies the current active filter criteria.
   *
   * Why: Ensures real-time incoming radio packets and historical database queries strictly
   * match active session, minimum SNR thresholds, and transmitting station isolation boundaries.
   *
   * @param packet - Target packet to evaluate
   * @returns boolean indicating if packet passes filters
   */
  matches(packet: StoredPacket): boolean {
    if (this.currentFilter.sessionId && packet.sessionId !== this.currentFilter.sessionId) {
      return false;
    }

    if (packet.snr < this.currentFilter.minSnr) {
      return false;
    }

    if (this.currentFilter.isolatedNodeId !== null) {
      const target = this.currentFilter.isolatedNodeId >>> 0;
      const tx = this.mapManager.resolveTransmittingNode(packet);

      // Strict transmitter match: only packets radiated by this node station
      // (either direct 0-hop transmission or resolved last-hop mesh relay)
      if ((tx.nodeId >>> 0) !== target) {
        return false;
      }
    }

    return true;
  }

  /**
   * Queries IndexedDB for matching packets and triggers map layer re-rendering.
   */
  async applyFilters(): Promise<StoredPacket[]> {
    const db = await getDb();
    let packets: StoredPacket[];

    if (this.currentFilter.sessionId) {
      packets = await db.getAllFromIndex('packets', 'by-session', this.currentFilter.sessionId);
    } else {
      packets = await db.getAll('packets');
    }

    const filtered = packets.filter((p) => this.matches(p));
    this.mapManager.renderPackets(filtered);
    return filtered;
  }

  getFilterState(): FilterState {
    return { ...this.currentFilter };
  }
}
